import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { getEvolutionTierColor } from '@solante/shared';
import { ShareableCard } from '../ShareableCard';
import { XTwitterIcon } from '../icons';
import { detectShareableMoment, ShareableStats } from '../../../services/shareService';
import { shortKey } from '../../../utils/format';

interface Evolution {
  wallet: string;
  fromJoker: { id: string; name: string; tier: string };
  toJoker: { id: string; name: string; tier: string };
}

interface PostGameSummaryProps {
  isWinner: boolean;
  myScore: number;
  opponentScore: number;
  evolutions: Evolution[];
  myWallet?: string;
  xpGained?: number;
  eloChange?: number;
  newElo?: number;
  totalPot?: string;
  winnerPayout?: string;
  currency?: string;
  onPlayAgain: () => void;
  onMainMenu: () => void;
  // Rematch props
  onRematchRequest?: () => void;
  rematchRequested?: boolean;
  opponentRematchRequested?: boolean;
  rematchConfirmed?: boolean;
  // Double-or-nothing props
  onDoubleOrNothingRequest?: () => void;
  doubleOrNothingProposed?: boolean;
  doubleOrNothingProposedByMe?: boolean;
  doubleOrNothingConfirmed?: boolean;
  doubleOrNothingWagerAmount?: string;
  // Loss mitigation
  badBeatBonus?: boolean;
}

/* ---------- Derived-string helpers (avoids deep ternary chains in JSX) ---------- */

function getRematchMessage(confirmed?: boolean, requested?: boolean, opponentRequested?: boolean) {
  if (confirmed) return 'Creating new match...';
  if (requested && opponentRequested) return 'Both players accepted!';
  if (requested) return 'Waiting for opponent...';
  if (opponentRequested) return 'Opponent wants a rematch!';
  return 'Challenge your opponent again';
}

function getRematchButtonClass(
  confirmed?: boolean,
  requested?: boolean,
  opponentRequested?: boolean,
) {
  if (confirmed) return 'bg-green-500/30 text-green-300 cursor-not-allowed';
  if (requested) return 'bg-purple-500/30 text-purple-300 cursor-not-allowed';
  if (opponentRequested) return 'bg-green-500 text-white hover:bg-green-600 animate-pulse';
  return 'bg-purple-500 text-white hover:bg-purple-600';
}

function getRematchButtonLabel(
  confirmed?: boolean,
  requested?: boolean,
  opponentRequested?: boolean,
) {
  if (confirmed) return '✓ Confirmed';
  if (requested) return 'Requested';
  if (opponentRequested) return 'Accept Rematch';
  return 'Request Rematch';
}

function getDoubleOrNothingMessage(
  confirmed?: boolean,
  proposed?: boolean,
  proposedByMe?: boolean,
  wagerAmount?: string,
  currency?: string,
  isWinner?: boolean,
) {
  if (confirmed) return 'Creating new match with 2x stakes...';
  if (proposed && proposedByMe) return 'Waiting for opponent to accept...';
  if (proposed && !proposedByMe)
    return `Opponent challenges you! ${wagerAmount || '2x'} ${currency}`;
  if (isWinner) return 'Challenge loser to 2x stakes!';
  return 'Waiting for winner to propose...';
}

export const PostGameSummary: React.FC<PostGameSummaryProps> = ({
  isWinner,
  myScore,
  opponentScore,
  evolutions,
  myWallet,
  xpGained,
  eloChange,
  newElo,
  totalPot,
  winnerPayout,
  currency,
  onPlayAgain,
  onMainMenu,
  onRematchRequest,
  rematchRequested,
  opponentRematchRequested,
  rematchConfirmed,
  onDoubleOrNothingRequest,
  doubleOrNothingProposed,
  doubleOrNothingProposedByMe,
  doubleOrNothingConfirmed,
  doubleOrNothingWagerAmount,
  badBeatBonus,
}) => {
  // Filter evolutions to show only the current player's evolutions
  const myEvolutions = myWallet ? evolutions.filter((e) => e.wallet === myWallet) : evolutions;

  // Share state
  const [showShareCard, setShowShareCard] = useState(false);

  // Detect if this is a shareable moment
  const shareType = detectShareableMoment({
    score: myScore,
    opponentScore,
    isWinner,
  });

  const shareableStats: ShareableStats | null = shareType
    ? {
        type: shareType,
        score: myScore,
        opponentScore,
        isWinner,
        playerName: myWallet ? shortKey(myWallet) : 'Player',
      }
    : null;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="arcade-panel w-full max-w-md space-y-4 p-4 sm:space-y-5 sm:p-6 overflow-y-auto max-h-[85vh]"
    >
      {/* Victory/Defeat Header */}
      <div className="text-center space-y-2">
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className={`text-3xl sm:text-4xl ${isWinner ? '' : 'grayscale'}`}
        >
          {isWinner ? '🏆' : '💀'}
        </motion.div>
        <h2
          className={`font-display text-2xl font-bold sm:text-3xl ${isWinner ? 'neon-text' : 'text-red-400'}`}
        >
          {isWinner ? 'VICTORY!' : 'DEFEAT'}
        </h2>
      </div>

      {/* Score Summary */}
      <div className="flex items-center justify-center gap-4 py-2 sm:gap-6 sm:py-3">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-white/50 mb-1">You</p>
          <p
            className={`font-display text-2xl font-bold ${isWinner ? 'text-neon-400' : 'text-white/70'}`}
          >
            {myScore}
          </p>
        </div>
        <div className="text-white/30 font-display text-lg">vs</div>
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-white/50 mb-1">Opponent</p>
          <p
            className={`font-display text-2xl font-bold ${!isWinner ? 'text-red-400' : 'text-white/70'}`}
          >
            {opponentScore}
          </p>
        </div>
      </div>

      {/* Payout Section */}
      {(totalPot || winnerPayout) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl border border-neon-400/30 bg-neon-500/10 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-xs uppercase tracking-widest text-white/50 mb-1">Total Pot</p>
              <p className="font-display text-lg font-bold text-white">
                {totalPot ?? '—'} {currency}
              </p>
            </div>
            <div className="h-8 w-px bg-white/20" />
            <div className="text-center flex-1">
              <p className="text-xs uppercase tracking-widest text-white/50 mb-1">
                {isWinner ? 'You Won' : 'Winner Payout'}
              </p>
              <p
                className={`font-display text-lg font-bold ${isWinner ? 'text-neon-400' : 'text-white/70'}`}
              >
                {winnerPayout ?? '—'} {currency}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Evolutions Section */}
      {myEvolutions.length > 0 && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">⚡</span>
            <h3 className="font-display text-sm font-semibold text-amber-300 uppercase tracking-wide">
              Evolutions This Match
            </h3>
          </div>
          <div className="space-y-2">
            {myEvolutions.map((evolution, index) => {
              const tierColor = getEvolutionTierColor(
                evolution.toJoker.tier as 'awakened' | 'ascended',
              );
              const tierLabel = evolution.toJoker.tier === 'ascended' ? 'ASCENDED' : 'AWAKENED';

              return (
                <motion.div
                  key={`${evolution.fromJoker.id}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="text-white/70">{evolution.fromJoker.name}</span>
                  <span className="text-amber-400">→</span>
                  <span className="font-semibold text-white">{evolution.toJoker.name}</span>
                  <span
                    className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
                    style={{ backgroundColor: tierColor, color: '#000' }}
                  >
                    {tierLabel}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bad Beat Bonus Message */}
      {badBeatBonus && !isWinner && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-center"
        >
          <span className="text-sm text-emerald-300">
            Bad Beat Bonus! You played well and earned extra XP.
          </span>
        </motion.div>
      )}

      {/* XP/ELO Section */}
      {(xpGained !== undefined || eloChange !== undefined) && (
        <div className="flex items-center justify-center gap-4 py-2 text-sm">
          {xpGained !== undefined && xpGained > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-1 text-emerald-300"
            >
              <span>
                +{xpGained} XP{badBeatBonus && !isWinner ? ' (incl. bonus)' : ''}
              </span>
            </motion.div>
          )}
          {eloChange !== undefined && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`flex items-center gap-1 ${eloChange >= 0 ? 'text-neon-300' : 'text-red-300'}`}
            >
              <span>ELO: {newElo ?? '?'}</span>
              <span className="text-xs">
                ({eloChange >= 0 ? '+' : ''}
                {eloChange})
              </span>
            </motion.div>
          )}
        </div>
      )}

      {/* Rematch Section */}
      {onRematchRequest && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl border border-purple-400/30 bg-purple-500/10 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-sm font-semibold text-purple-300 uppercase tracking-wide">
                Rematch
              </h3>
              <p className="text-xs text-white/50 mt-1">
                {getRematchMessage(rematchConfirmed, rematchRequested, opponentRematchRequested)}
              </p>
            </div>
            <button
              type="button"
              onClick={onRematchRequest}
              disabled={rematchRequested || rematchConfirmed}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${getRematchButtonClass(rematchConfirmed, rematchRequested, opponentRematchRequested)}`}
            >
              {getRematchButtonLabel(rematchConfirmed, rematchRequested, opponentRematchRequested)}
            </button>
          </div>
        </motion.div>
      )}

      {/* Double-or-Nothing Section */}
      {onDoubleOrNothingRequest && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-sm font-semibold text-amber-300 uppercase tracking-wide">
                Double or Nothing
              </h3>
              <p className="text-xs text-white/50 mt-1">
                {getDoubleOrNothingMessage(
                  doubleOrNothingConfirmed,
                  doubleOrNothingProposed,
                  doubleOrNothingProposedByMe,
                  doubleOrNothingWagerAmount,
                  currency,
                  isWinner,
                )}
              </p>
              {doubleOrNothingWagerAmount && (
                <p className="text-[10px] text-amber-300/70 mt-0.5">
                  New wager: {doubleOrNothingWagerAmount} {currency}
                </p>
              )}
            </div>
            {/* Only show button if:
                - Winner can propose (not yet proposed)
                - Loser can accept (when proposed by opponent) */}
            {(isWinner && !doubleOrNothingProposed) ||
            (!isWinner && doubleOrNothingProposed && !doubleOrNothingProposedByMe) ? (
              <button
                type="button"
                onClick={onDoubleOrNothingRequest}
                disabled={doubleOrNothingConfirmed}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  doubleOrNothingConfirmed
                    ? 'bg-green-500/30 text-green-300 cursor-not-allowed'
                    : !isWinner && doubleOrNothingProposed
                      ? 'bg-amber-500 text-white hover:bg-amber-600 animate-pulse'
                      : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                {doubleOrNothingConfirmed
                  ? '✓ Confirmed'
                  : isWinner
                    ? 'Propose 2x'
                    : 'Accept Challenge'}
              </button>
            ) : doubleOrNothingProposedByMe ? (
              <span className="text-xs text-amber-300/70">Proposed</span>
            ) : null}
          </div>
        </motion.div>
      )}

      {/* Share Button */}
      {shareableStats && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            type="button"
            onClick={() => setShowShareCard(true)}
            className="w-full rounded-lg border border-blue-400/30 bg-blue-500/10 px-4 py-2.5 text-sm font-semibold text-blue-300 transition hover:bg-blue-500/20 flex items-center justify-center gap-2"
          >
            <XTwitterIcon className="w-4 h-4" />
            Share Your {isWinner ? 'Victory' : 'Score'}
          </button>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 pt-2 sm:gap-3">
        <button type="button" onClick={onPlayAgain} className="arcade-btn flex-1">
          Play Again
        </button>
        <button
          type="button"
          onClick={onMainMenu}
          className="flex-1 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/20"
        >
          Main Menu
        </button>
      </div>

      {/* Share Modal */}
      {showShareCard && shareableStats && (
        <ShareableCard stats={shareableStats} onClose={() => setShowShareCard(false)} />
      )}
    </motion.div>
  );
};
